/* verqo — "Only on verqo" preview page
   Standalone /preview/why-verqo route for design review BEFORE merging
   the bento bands into the live landing.
   Layout: header bar → seller bento (8 tiles) → buyer bento (8 tiles) →
   coming-soon teaser strip → footer bar.
*/
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Banknote, ShieldCheck, Repeat, Wallet, Users, ScrollText, Boxes, Globe,
  CreditCard, ShoppingCart, Sparkles, MousePointerClick, FileSignature,
  Truck, LayoutDashboard, Network, Plane, Bot, RotateCw, Zap,
  ArrowRight, CircleDot,
} from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";

/* ─────────────────────────────────────────────────────────── */
/* Atoms                                                       */
/* ─────────────────────────────────────────────────────────── */
const Eyebrow = ({ children, testid }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700" data-testid={testid}>
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

/* Bento tile primitive.
   variant: "hero" (large lime tile · spans 2 cols on lg) | "wide" (spans 2 cols) | "default"
*/
function Tile({ icon: Icon, n, title, body, variant = "default", testid }) {
  const base = "relative rounded-2xl border p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 group";
  const styles = {
    hero:    "bg-[#dcff3a] border-lime-400/50 text-slate-900 lg:col-span-2 lg:row-span-2 shadow-[0_20px_60px_-30px_rgba(34,80,8,0.45)]",
    wide:    "bg-white border-slate-200/80 lg:col-span-2 hover:shadow-[0_24px_60px_-32px_rgba(15,23,42,0.18)] hover:border-slate-300",
    default: "bg-white border-slate-200/80 hover:shadow-[0_24px_60px_-32px_rgba(15,23,42,0.18)] hover:border-slate-300",
  };
  const iconWrap = variant === "hero"
    ? "w-12 h-12 rounded-xl bg-slate-900 text-lime-300 grid place-items-center shadow-md"
    : "w-11 h-11 rounded-xl bg-slate-900/[0.04] text-slate-700 grid place-items-center group-hover:bg-lime-100 group-hover:text-lime-800 transition-colors";

  return (
    <div className={`${base} ${styles[variant]}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-5">
        <div className={iconWrap}>
          <Icon className={variant === "hero" ? "w-6 h-6" : "w-5 h-5"} strokeWidth={1.6} />
        </div>
        <span className={`mono text-[10px] tracking-[0.2em] ${variant === "hero" ? "text-slate-900/50" : "text-slate-400"}`}>
          {String(n).padStart(2, "0")}
        </span>
      </div>
      <h3 className={`font-semibold tracking-tight ${variant === "hero" ? "text-[28px] md:text-[32px] leading-[1.05]" : "text-[19px] md:text-[20px] leading-[1.15]"}`}>
        {title}
      </h3>
      <p className={`mt-3 ${variant === "hero" ? "text-[15px] md:text-[16px] text-slate-900/75 leading-relaxed" : "text-[13.5px] text-slate-600 leading-relaxed"}`}>
        {body}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Data — 8 seller tiles + 8 buyer tiles + 5 coming-soon       */
/* ─────────────────────────────────────────────────────────── */
const SELLER_TILES = [
  { icon: Banknote,        title: "Same-day cash on every invoice",  body: "The moment your buyer's PO is approved, we wire the invoice amount straight to your wallet. Net-30/60/90 disappears.", variant: "hero" },
  { icon: ShieldCheck,     title: "Zero default risk — Allianz baked in", body: "Every funded invoice is insured by Allianz Trade. The cover lives inside the platform — no separate policy to manage.", variant: "wide" },
  { icon: Repeat,          title: "Never chase a payment again", body: "If a buyer goes 60 days past due, Allianz takes over. You keep the cash. No collections team. No write-offs.", variant: "default" },
  { icon: Wallet,          title: "D+1 wallet → bank", body: "Your wallet sweeps to your bank automatically on the schedule you set. Or hit withdraw anytime.", variant: "default" },
  { icon: Users,           title: "Buyer 360° drawer", body: "Score · Allianz cover · payment history · open orders · open invoices — every buyer, one panel.", variant: "default" },
  { icon: ScrollText,      title: "PO inbox parser", body: "Drop a PDF, forward an email, paste a CSV, or hit the API. We extract every line, match SKUs, draft the order.", variant: "default" },
  { icon: Boxes,           title: "Dropship Automation", body: "One toggle connects a buyer's Shopify store. Orders auto-fulfill daily, tracking auto-pushes, invoices auto-issue.", variant: "default" },
  { icon: Globe,           title: "Your portal, your brand", body: "Custom domain (shop.yourbrand.com), your logo, your colors. Buyers feel like they're in your store — not ours.", variant: "default" },
];

const BUYER_TILES = [
  { icon: Network,         title: "One credit line. Every supplier.", body: "Apply once. Use it across every brand on verqo. No re-applying. No paper trade references. No fax-back forms.", variant: "hero" },
  { icon: CreditCard,      title: "Net-30/60/90 on every invoice", body: "Buy now, pay later — at every supplier on the network, on the terms each seller offers you.", variant: "wide" },
  { icon: Sparkles,        title: "Approved in under 60 seconds", body: "Plaid your bank, KYB your EIN. Our AI underwriter drafts your memo and unlocks your line in real time.", variant: "default" },
  { icon: MousePointerClick, title: "One click. Seller paid. Goods released.", body: "No bank wires. No ACH delays. Hit Pay and your supplier ships instantly.", variant: "default" },
  { icon: FileSignature,   title: "Buyer PO Generator", body: "Build a PO in 30 seconds — line items, payment terms, ship-to. Send to any supplier on or off verqo.", variant: "default" },
  { icon: Truck,           title: "Book shipping inside the portal", body: "UPS · FedEx · USPS · LTL freight. Rates, labels, live tracking — all in your verqo ledger.", variant: "default" },
  { icon: LayoutDashboard, title: "One Payments tab for everything", body: "Wallet · Credit · Methods · Activity — one screen for your money, your terms, and your history.", variant: "default" },
  { icon: Boxes,           title: "Dropship Automation", body: "Sell on Shopify? Connect your store and your suppliers auto-fulfill daily. You never touch a label.", variant: "default" },
];

const COMING_SOON = [
  { icon: Plane,   title: "Cross-border with trust",          eta: "2026" },
  { icon: Zap,     title: "Wallet-to-wallet instant settle",  eta: "Q2 2026" },
  { icon: Bot,     title: "verqo AI Assistant",               eta: "Q2 2026" },
  { icon: RotateCw,title: "Dynamic credit recalibration",     eta: "Q3 2026" },
  { icon: FileSignature, title: "Typed e-signature on credit acceptance", eta: "Q2 2026" },
];

/* ─────────────────────────────────────────────────────────── */
/* Page                                                        */
/* ─────────────────────────────────────────────────────────── */
export default function WhyVerqoPreview() {
  useEffect(() => { document.title = "Only on verqo · Preview"; }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }} data-testid="why-verqo-preview">
      {/* Preview-only top bar */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2 cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 rounded-md"
            aria-label="Back to top"
            data-testid="preview-brand-home"
          >
            <VerqoLockup />
            <span className="ml-1 text-[11px] text-slate-500 hidden md:inline group-hover:text-slate-900 transition-colors">· Only on verqo · preview</span>
          </button>
          <a
            href="/preview/landing-v2"
            data-testid="preview-back-to-landing"
            className="text-[12px] text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1.5"
          >
            ← Back to landing
          </a>
        </div>
      </header>

      {/* ═══════════════ Preview banner ═══════════════ */}
      <div className="pt-24 pb-2 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-[11px] uppercase tracking-[0.2em] text-amber-800" data-testid="preview-banner">
          <CircleDot className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
          PREVIEW — NOT YET LIVE ON HOMEPAGE
        </div>
      </div>

      {/* ═══════════════ Section: For sellers ═══════════════ */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-only-on-verqo-sellers">
        <Reveal>
          <Eyebrow testid="seller-eyebrow">Only on verqo · for sellers</Eyebrow>
        </Reveal>
        <Reveal delay={0.06}>
          <h2 className="mt-5 text-[36px] md:text-[52px] leading-[1.02] tracking-tight font-semibold text-slate-900 max-w-3xl">
            Eight things <span className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block">no one else</span><br /> gives wholesale sellers.
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-5 text-[16px] md:text-[18px] text-slate-600 max-w-2xl leading-relaxed">
            Every BNPL, factoring company, and TMS in the market fixes one slice of the wholesale stack.
            verqo is the only one that gives you all eight in one ledger.
          </p>
        </Reveal>

        <div
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-[1fr]"
          data-testid="seller-bento"
        >
          {SELLER_TILES.map((t, i) => (
            <Tile
              key={`s-${i}`}
              n={i + 1}
              icon={t.icon}
              title={t.title}
              body={t.body}
              variant={t.variant}
              testid={`seller-tile-${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ═══════════════ Section: For buyers ═══════════════ */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 bg-slate-50/50 border-y border-slate-100" data-testid="section-only-on-verqo-buyers">
        <div className="max-w-[1280px] mx-auto">
          <Reveal>
            <Eyebrow testid="buyer-eyebrow">Only on verqo · for buyers</Eyebrow>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-5 text-[36px] md:text-[52px] leading-[1.02] tracking-tight font-semibold text-slate-900 max-w-3xl">
              Eight things <span className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block">retailers and resellers</span><br /> have been waiting for.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 text-[16px] md:text-[18px] text-slate-600 max-w-2xl leading-relaxed">
              One portable credit line. Sixty-second approvals. Goods released the moment you click pay.
              Wholesale buying without the paper, the phone calls, or the wire delays.
            </p>
          </Reveal>

          <div
            className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-[1fr]"
            data-testid="buyer-bento"
          >
            {BUYER_TILES.map((t, i) => (
              <Tile
                key={`b-${i}`}
                n={i + 1}
                icon={t.icon}
                title={t.title}
                body={t.body}
                variant={t.variant}
                testid={`buyer-tile-${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ Section: Coming soon ═══════════════ */}
      <section className="relative py-24 md:py-28 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-coming-soon">
        <div className="grid lg:grid-cols-[1fr_1.6fr] gap-12 items-start">
          <Reveal>
            <div>
              <Eyebrow>What's next on verqo</Eyebrow>
              <h2 className="mt-5 text-[32px] md:text-[40px] leading-[1.05] tracking-tight font-semibold text-slate-900">
                Five things <span className="italic text-slate-500">shipping next.</span>
              </h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
                Our roadmap isn't a wish list — it's what's already in build behind feature flags. Here's what flips on in the next two quarters.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-3" data-testid="coming-soon-pills">
              {COMING_SOON.map((c, i) => (
                <div
                  key={i}
                  className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-slate-300 bg-white hover:border-slate-900 hover:shadow-sm transition-all"
                  data-testid={`coming-soon-pill-${i + 1}`}
                >
                  <c.icon className="w-4 h-4 text-slate-700" strokeWidth={1.6} />
                  <span className="text-[13px] font-medium text-slate-900">{c.title}</span>
                  <span className="text-[10px] mono uppercase tracking-[0.2em] text-lime-700 bg-lime-50 border border-lime-200 px-1.5 py-0.5 rounded-full">{c.eta}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ Footer-style CTA bar ═══════════════ */}
      <section className="relative py-20 md:py-28 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-preview-cta">
        <Reveal>
          <div className="rounded-3xl bg-slate-900 text-white px-8 md:px-14 py-12 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <Eyebrow>End of preview</Eyebrow>
              <h3 className="mt-3 text-[26px] md:text-[34px] font-semibold leading-tight tracking-tight">
                Approve this to merge into the live homepage.
              </h3>
              <p className="mt-3 text-[14px] text-slate-300 max-w-xl leading-relaxed">
                Once approved, the seller bento goes between the "How it works" and "For buyers" sections, the buyer bento goes inside the "For buyers" band, and the coming-soon strip slots before the FAQ.
              </p>
            </div>
            <a
              href="/preview/landing-v2"
              data-testid="preview-cta-back"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-lime-300 text-slate-900 text-sm font-semibold hover:bg-lime-200 transition-colors"
            >
              Back to live landing <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
